import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  TextField,
  Stack,
  Chip
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useAuth, getToken } from '../../components/auth/AuthContext';
import { APIHOST } from '../../common';

export default function UserProfilePage() {
  const navigate = useNavigate();
  const { currentUser, isLoggedIn, login } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!isLoggedIn || !currentUser) {
      navigate('/');
      return;
    }
    
    // Initialize form with current user data
    setFirstName(currentUser.firstName || '');
    setLastName(currentUser.lastName || '');
  }, [isLoggedIn, currentUser, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!firstName.trim() || !lastName.trim()) {
      setError('First name and last name are required');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/users/updateMe`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim()
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to update profile');
      }

      const data = await response.json();
      
      // Update the auth context with new user data
      const updatedAuthData = {
        jwt: token,
        user: data
      };
      login(updatedAuthData);
      
      setSuccess('Profile updated successfully!');

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
      console.error('Profile update error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isLoggedIn || !currentUser) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Alert severity="warning">
          Please log in to view your profile.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', py: 4, my: 4}}>
      <Container maxWidth="md">
        <Box sx={{ mb: 3 }}>
          <Typography variant="h3" component="h1" gutterBottom>
            My Profile
          </Typography>
          <Typography variant="subtitle1">
            Update your personal information
          </Typography>
        </Box>
        
        <Paper sx={{ p: 4 }}>
          <Stack spacing={3}>
            {/* User Type Display */}
            {currentUser.userType !== 'user' && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Account Type
                </Typography>
                <Chip 
                  label={currentUser.userType?.toUpperCase() || 'USER'} 
                  color="primary" 
                  variant="outlined"
                />
              </Box>
            )}

            {/* Email Display (read-only) */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Email Address
              </Typography>
              <Typography variant="body1" sx={{ p: 2, borderRadius: 1 }}>
                {currentUser.email}
              </Typography>
            </Box>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  label="First Name"
                  variant="outlined"
                  fullWidth
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={loading}
                  required
                />

                <TextField
                  label="Last Name"
                  variant="outlined"
                  fullWidth
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={loading}
                  required
                />

                {/* Error Display */}
                {error && (
                  <Alert severity="error">
                    {error}
                  </Alert>
                )}

                {/* Success Display */}
                {success && (
                  <Alert severity="success">
                    {success}
                  </Alert>
                )}

                {/* Submit Button */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={loading}
                    size="large"
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </Stack>
            </form>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}